/**
 * What the public site says about each part of FactreeTech.
 *
 * One entry per page at `/features/:slug`. The header's Features menu, the home
 * page's module grid and the pricing page all read this list, so a module is
 * added in one place.
 *
 * Prices are never written here. `planKey` names the subscription plan
 * (`subscription_plans.module_key`) whose live price the page shows; an entry
 * with none is included with every account.
 *
 * Screenshots are `src/assets/marketing/<shot>.webp`, taken from the demo
 * organisation. A shot named here with no file is left out, never broken.
 */

import type { LucideIcon } from 'lucide-react';
import {
  Apple,
  Award,
  CloudSun,
  ClipboardCheck,
  ClipboardList,
  Clock,
  Droplets,
  FlaskConical,
  HardHat,
  Landmark,
  Leaf,
  ShoppingCart,
  Sprout,
  TreePine,
  Wrench,
} from 'lucide-react';

export interface FeatureHighlight {
  title: string;
  body: string;
}

export interface FeatureShot {
  /** File name in `src/assets/marketing`, without `.webp`. */
  shot: string;
  caption: string;
}

export interface Feature {
  slug: string;
  /** Menu and card title. */
  name: string;
  icon: LucideIcon;
  /** Tailwind classes for the icon tile. */
  tone: string;
  /** One line for cards and the menu. */
  summary: string;
  /** The page's headline. */
  headline: string;
  /** Two or three sentences under the headline. */
  intro: string;
  /** Who it is for, in a phrase. */
  forWhom: string;
  highlights: FeatureHighlight[];
  shots: FeatureShot[];
  /** `subscription_plans.module_key`; none means included with every account. */
  planKey?: string;
  /** What is free, said on the page beside the price. */
  freeNote?: string;
}

export const FEATURES: Feature[] = [
  {
    slug: 'farms',
    name: 'Farms & orchard maps',
    icon: TreePine,
    tone: 'bg-emerald-100 text-emerald-700',
    summary: 'Draw every farm, block and planting on the map, once, and everything else hangs off it.',
    headline: 'Your farm, mapped the way you actually manage it',
    intro:
      'Draw each farm, its blocks and the cultivar plantings inside them straight onto satellite imagery. Area, tree numbers and spacings are worked out for you, and every spray, job, bin and irrigation valve in FactreeTech knows which block it belongs to.',
    forWhom: 'Orchards, vineyards and nurseries of any size',
    highlights: [
      { title: 'Farm → block → planting', body: 'Boundaries drawn on the map, with hectares calculated from the outline. Plantings carry cultivar, rootstock, row and tree spacing, planting date and training system.' },
      { title: 'Tree counts that add up', body: 'Tree numbers are estimated from area and spacings, and you can override them with what was actually planted.' },
      { title: 'Harvest windows by season', body: 'Record when picking opens and closes for each planting, each season. The spray program checks withholding periods against them.' },
      { title: 'Growing regions', body: 'Pick an address and the farm is placed in its growing region, so maturity and harvest data compare like with like.' },
      { title: 'Archive, never lose history', body: 'A farm you no longer run is archived with its records intact, and its irrigation hardware is freed for use elsewhere.' },
      { title: 'Works on a phone in the paddock', body: 'Maps scroll with the page on a touch screen, and a tap hands the map back for panning when you want it.' },
    ],
    shots: [
      { shot: 'orchard', caption: 'Every farm at a glance: hectares, blocks and plantings.' },
      { shot: 'orchard-farm', caption: 'A farm with its blocks drawn on the map.' },
    ],
    freeNote: 'Farms, blocks and plantings are free on every account.',
  },
  {
    slug: 'timesheets',
    name: 'Timesheets, leave & payroll',
    icon: Clock,
    tone: 'bg-sky-100 text-sky-700',
    summary: 'Clock in on a phone, approve by supervisor, pay through Xero or MYOB.',
    headline: 'From clock-in to pay run without retyping a single hour',
    intro:
      'Staff clock in and out on their phone and ask for leave in the same app. Supervisors approve their own people, HR sees the whole pay cycle, and payroll sends approved hours and leave straight to Xero or MYOB.',
    forWhom: 'Employers with permanent, casual and seasonal staff',
    highlights: [
      { title: 'Free clock-in for everyone', body: 'Anyone in your organisation can clock in, clock out and request leave at no cost. You pay only for the people who approve and run payroll.' },
      { title: 'Short days, handled honestly', body: 'A day short of the roster offers two answers: "I forgot to clock in" or "I wasn\'t at work". Nobody burns leave for hours they actually worked.' },
      { title: 'Supervisor approvals', body: 'Each supervisor approves their own staff. Payroll can see who is still waiting and nudge them with one tap.' },
      { title: 'Leave balances that are right', body: 'Annual, personal and long-service leave accrue from approved hours. Time in lieu is only banked under an agreement the employee signs.' },
      { title: 'Payroll by API', body: 'Approved timesheets and leave are posted to Xero Payroll, and leave balances come back from Xero after each pay run. MYOB and file exports are there too.' },
      { title: 'Live attendance', body: 'See who is on site right now, who is on leave and who has not started yet.' },
    ],
    shots: [
      { shot: 'my-timesheet', caption: 'An employee\'s pay cycle, day by day against their roster.' },
      { shot: 'hr-paycycle', caption: 'The pay cycle dashboard for HR.' },
      { shot: 'payroll', caption: 'Payroll sees where approvals stand before the pay run.' },
    ],
    planKey: 'timesheet',
    freeNote: 'Clocking in and out and requesting leave are free for every staff member.',
  },
  {
    slug: 'spray',
    name: 'Spray management',
    icon: FlaskConical,
    tone: 'bg-violet-100 text-violet-700',
    summary: 'Plan, record and prove every spray: label rates, WHP, weather and GPS coverage.',
    headline: 'Spray records that stand up to an audit, and a withholding period that is actually checked',
    intro:
      'Plan a spray from the registered label, send it to the operator\'s phone, and get back a complete record: tank by tank, with the weather when it started and finished and a GPS map of where it went. Withholding periods are checked against your harvest dates before anyone fills a tank.',
    forWhom: 'Growers, spray contractors and agronomists',
    highlights: [
      { title: 'Rates from the label', body: 'Australian products come from the APVMA register, and rates are read from the approved label PDF. They are always reviewed by a person before they are used.' },
      { title: 'WHP checked against harvest', body: 'Every tank mix is checked against each block\'s harvest window. The longest WHP in the mix binds, and a conflict holds the spray until a Spray Admin clears it.' },
      { title: 'Weather, three times', body: 'The forecast when planned, the on-farm reading at start, and the reading at sign-off, each saying whether it came from your station or the forecast grid.' },
      { title: 'GPS coverage, tank by tank', body: 'The operator\'s phone records where the sprayer went. Each tank shows its rate per hectare and whether it went on light or heavy, and why.' },
      { title: 'Calibration on record', body: 'Calibrate a sprayer any time. A finished spray keeps the calibration it actually ran at, even after the machine is recalibrated.' },
      { title: 'Pest and disease forecasting', body: 'Infection models such as apple scab and Magarey run on your weather, with alerts and protection windows for each planting.' },
    ],
    shots: [
      { shot: 'spray', caption: 'Every spray, planned, running and finished.' },
      { shot: 'spray-chemicals', caption: 'A chemical with its APVMA registration, label and pack sizes.' },
      { shot: 'spray-pests', caption: 'Pests and diseases, with forecasting models and field guides.' },
    ],
    planKey: 'spray',
  },
  {
    slug: 'weather',
    name: 'Weather & disease risk',
    icon: CloudSun,
    tone: 'bg-amber-100 text-amber-700',
    summary: 'Your own weather station, or a modelled forecast, feeding sprays and disease alerts.',
    headline: 'The weather on your farm, not the nearest town',
    intro:
      'Pair an on-farm weather station and its readings flow into spray records, irrigation rain skips, job weather and the disease models. With no station, FactreeTech uses a modelled forecast for each farm and says so.',
    forWhom: 'Anyone deciding when to spray, irrigate or pick',
    highlights: [
      { title: 'Live station readings', body: 'Temperature, humidity, wind and gusts, rain, solar and soil readings, with history and charts.' },
      { title: 'Spray weather limits', body: 'Set limits for gusts, wind, temperature, Delta T and rain. A spray that would start outside them is flagged before it starts.' },
      { title: 'Disease risk', body: 'Infection periods are worked out from leaf wetness and temperature, with alerts when risk rises and when protection washes off.' },
      { title: 'Rain skip', body: 'Irrigation can skip a day after enough rain has fallen.' },
    ],
    shots: [
      { shot: 'weather', caption: 'Current conditions and the week ahead for a farm.' },
      { shot: 'weather-pests', caption: 'Disease risk and spray protection for each planting.' },
    ],
    planKey: 'weather_stations',
    freeNote: 'A modelled forecast is shown for every farm at no cost.',
  },
  {
    slug: 'irrigation',
    name: 'Irrigation & soil sensors',
    icon: Droplets,
    tone: 'bg-cyan-100 text-cyan-700',
    summary: 'Program valves and pumps, watch them run live, and stop the whole site in one tap.',
    headline: 'Know what is watering right now, and what runs next',
    intro:
      'Run irrigation programs on your own field controllers, see every valve and pump live on the farm map, and read soil moisture beside it. If something goes wrong, one button stops the site safely: pumps first, then the valves.',
    forWhom: 'Orchards and nurseries with automated irrigation',
    highlights: [
      { title: 'Programs with several starts', body: 'Water the same blocks more than once a day, with each start at its own length.' },
      { title: 'Live farm view', body: 'Every valve that should be on and every valve that is on, with a "did not start" alarm when a valve should be open and the controller says it is not.' },
      { title: 'Stop everything, safely', body: 'Pumps are confirmed off before any valve closes, so a running pump is never shut in. Watering stays held until an admin resumes it.' },
      { title: 'Flow from what is installed', body: 'Describe the drip or sprinklers in a planting and each valve\'s flow is worked out for the hydraulic model.' },
      { title: 'Alerts to the right people', body: 'Faults and offline controllers go to the people named for that farm, not everyone.' },
      { title: 'Pump hours, automatically', body: 'A pump\'s run time is credited to its equipment record, so its services fall due on time.' },
    ],
    shots: [
      { shot: 'irrigation', caption: 'What is watering now and what is due next.' },
      { shot: 'irrigation-schedule', caption: 'A program that runs three times a day, each start at its own length.' },
    ],
    planKey: 'irrigation',
  },
  {
    slug: 'farm-jobs',
    name: 'Farm jobs, labour & harvest',
    icon: ClipboardList,
    tone: 'bg-orange-100 text-orange-700',
    summary: 'Plan the work, run crews, cost every hour to a block, and track bins to the packhouse.',
    headline: 'Where the hours went, what they cost, and what came off the trees',
    intro:
      'Plan jobs against blocks, send them to phones, and run crews from a supervisor\'s phone, even out of signal. Every hour lands on a block, so cost per hectare, per bin and per tonne comes out of the work itself. At harvest, bins are started in the row and tracked through the shed to the packhouse.',
    forWhom: 'Managers running crews, contractors and harvest',
    highlights: [
      { title: 'Crew mode', body: 'A supervisor starts, moves and stops a whole crew from one phone. It works through dead spots and catches up when signal returns.' },
      { title: 'Hours per block', body: 'Time is recorded against the planting it was spent on, and checked against timesheets so nothing is counted twice or missed.' },
      { title: 'Labour costing', body: 'Set your own cost rates by kind of worker or labour provider, never per person. See hours and dollars per hectare, per bin and per tonne.' },
      { title: 'Bins from row to packhouse', body: 'Bins are started in the row with a GPS fix, labelled at the shed, loaded onto dockets and matched to packouts by grade.' },
      { title: 'Budgets and crop estimates', body: 'Budget each block by season, copy last season forward, and watch harvest progress against the estimate.' },
      { title: 'Harvest dates feed back', body: 'Actual picking dates from bins update harvest windows, and yield history suggests next season\'s estimate.' },
    ],
    shots: [
      { shot: 'jobs', caption: 'Jobs across the farm, by status.' },
      { shot: 'jobs-labour', caption: 'Hours and costs by block, work type and provider.' },
      { shot: 'jobs-insights', caption: 'Season insights: budget against actual, and harvest progress.' },
      { shot: 'harvest-shed', caption: 'The shed: receive, label and load bins.' },
    ],
    planKey: 'farm_jobs',
    freeNote: 'Pickers need no seat, and supervisor seats are free.',
  },
  {
    slug: 'equipment',
    name: 'Equipment & maintenance',
    icon: Wrench,
    tone: 'bg-slate-200 text-slate-700',
    summary: 'Every machine, its services, pre-starts, parts, insurance and who has it.',
    headline: 'Every machine serviced on time, and nobody driving one they aren\'t trained on',
    intro:
      'One register for tractors, sprayers, vehicles, pumps and hand tools. Services fall due by hours or date, pre-start checks are done on the phone, and a machine can only be started by someone inducted on it.',
    forWhom: 'Farms with a fleet of any size',
    highlights: [
      { title: 'Service intervals', body: 'By hours, kilometres or date, with reminders when one falls due.' },
      { title: 'Pre-start checks', body: 'Checklists done on the phone before a machine is used, kept as a record.' },
      { title: 'Inductions enforced', body: 'Nobody is put on a job with a machine they are not signed off on. The tractor and the implement behind it are checked separately.' },
      { title: 'Who has it', body: 'Issue gear to a person or crew, standing or on loan, with a reminder when a loan is overdue.' },
      { title: 'Insurance', body: 'Policies over many machines, renewed each year, with reminders before they lapse and an asset schedule for your broker.' },
      { title: 'Parts and service history', body: 'Filters, oils and parts for each machine, and every service it has had.' },
    ],
    shots: [
      { shot: 'equipment', caption: 'The equipment register.' },
      { shot: 'equipment-detail', caption: 'A pump with its hours counted from its relay, and who it is issued to.' },
      { shot: 'equipment-insurance', caption: 'Insurance policies and what they cover.' },
    ],
    planKey: 'vehicles',
  },
  {
    slug: 'nursery',
    name: 'Nursery',
    icon: Sprout,
    tone: 'bg-lime-100 text-lime-700',
    summary: 'Stock in the rows, orders on a sheet, loads picked by phone, invoices at despatch.',
    headline: 'From the row to the truck, with only picked trees invoiced',
    intro:
      'Know what is growing in every row, take orders on a fast order sheet or import them from retailers, and plan loads by delivery zone. Pickers pick on their phone, short picks become backorders, and invoices are raised from what was actually picked.',
    forWhom: 'Production and retail nurseries',
    highlights: [
      { title: 'Stock where it stands', body: 'Lots by block, row, side and position, with stage, ready date and a QR label.' },
      { title: 'The order sheet', body: 'Every product on one page with its free stock. Type quantities straight down the sheet, scan barcodes at the counter, and it saves as you go.' },
      { title: 'Despatch planning', body: 'Orders grouped by zone and window, put on loads, and checked for stock that needs moving first.' },
      { title: 'Picking on a phone', body: 'One customer at a time in walk order. Two pickers never pick the same customer, and nobody on the floor sees a price.' },
      { title: 'Retailer purchase orders', body: 'Import retailer purchase order files and cross dock schedules. Anything not right is held with the reason.' },
      { title: 'Counter sales', body: 'Walk-in sales paid by card through a QR code on your own Stripe account, or cash into a till.' },
    ],
    shots: [
      { shot: 'nursery', caption: 'The nursery at a glance.' },
      { shot: 'nursery-ordersheet', caption: 'The order sheet: every product with its free stock.' },
      { shot: 'nursery-despatch', caption: 'Despatch planning by zone and window.' },
      { shot: 'nursery-lots', caption: 'Lots in the rows, by stage.' },
    ],
    planKey: 'nursery',
    freeNote: 'Order picker seats are free.',
  },
  {
    slug: 'accounts',
    name: 'Accounts',
    icon: Landmark,
    tone: 'bg-teal-100 text-teal-700',
    summary: 'Customers, price lists, invoices, receivables and rebates, connected to Xero.',
    headline: 'Get paid for what left the gate',
    intro:
      'Customers with head offices and sites, price lists that say where each price came from, invoices that can only change by credit note, and aged receivables by payer. Invoices and credit notes go to Xero; counter cash is sent there, and other payments come back.',
    forWhom: 'Businesses selling on account',
    highlights: [
      { title: 'Head offices and sites', body: 'A chain orders and is shipped per store, and the head office pays for all of them, less its rebate.' },
      { title: 'Price lists', body: 'Per customer, per head office, per tier or base, consulted in that order, and every price says where it came from.' },
      { title: 'Receivables', body: 'Aged debtors by payer, short payments accepted or disputed, and statements with your own terms of trade.' },
      { title: 'Rebates', body: 'Agreements per payer, credited on each invoice as it is authorised, so the balance is what will be paid.' },
      { title: 'Xero and MYOB', body: 'One connection for invoices and payroll, set up once by an Accounts Admin.' },
      { title: 'Your logo on your paperwork', body: 'Invoices, statements, dockets and pick lists carry your logo and details.' },
    ],
    shots: [
      { shot: 'accounts', caption: 'The accounts dashboard.' },
      { shot: 'accounts-receivables', caption: 'Receivables by payer, with ageing.' },
      { shot: 'accounts-invoices', caption: 'Invoices and their payment state.' },
    ],
    planKey: 'accounts',
  },
  {
    slug: 'cultivar-registry',
    name: 'Cultivar registry',
    icon: Leaf,
    tone: 'bg-green-100 text-green-700',
    summary: 'Cultivars, plant breeder\'s rights, licences, contracts and maturity data.',
    headline: 'Every variety you grow or license, with the paperwork behind it',
    intro:
      'Record cultivars with their protection, breeder and characteristics, issue and sign licences on versioned contracts, and build maturity and harvest timing from real orchard observations.',
    forWhom: 'Licensors, propagators and growers of protected varieties',
    highlights: [
      { title: 'PBR and patents', body: 'Protection records with numbers, dates and authorities, researched for you and reviewed before saving.' },
      { title: 'Licences on fixed contracts', body: 'Contracts are versioned, and nobody picks a version: what was previewed is what is sent, fingerprinted and signed.' },
      { title: 'Evaluations', body: 'Orchard visits record fruit, crop load and maturity, and roll up by region and season.' },
      { title: 'Maturity charts', body: 'When each variety is picked, by region, with harvest windows drawn across seasons.' },
      { title: 'Talking points', body: 'A short description of each cultivar for staff and customers, and optionally for your website.' },
      { title: 'Private by default', body: 'Your cultivars are yours. Others see them only through a licence, a share or when you make them public.' },
    ],
    shots: [
      { shot: 'cultivar', caption: 'The cultivar registry.' },
      { shot: 'cultivar-detail', caption: 'What to tell a customer about each cultivar.' },
    ],
    planKey: 'cultivar',
  },
  {
    slug: 'breeder',
    name: 'Breeders & variety owners',
    icon: Award,
    tone: 'bg-rose-100 text-rose-700',
    summary: 'Track your varieties in the field: sub-licences, plantings and performance.',
    headline: 'See where your varieties are planted and how they perform',
    intro:
      'For breeders and variety owners. Follow sub-licences issued in each region, plantings on an anonymised map, and evaluation data from the orchards growing them, and control release region by region through nominated licensors.',
    forWhom: 'Breeding programs and variety owners',
    highlights: [
      { title: 'Sub-licence dashboard', body: 'How many licences each variety has, where, and their status.' },
      { title: 'Anonymised planting map', body: 'Where your varieties are in the ground, without exposing whose orchard it is.' },
      { title: 'Evaluation data', body: 'Fruit and maturity observations from the field, by region and season.' },
      { title: 'Release control', body: 'Nominate a licensor per region and decide when a variety is released there.' },
    ],
    shots: [
      { shot: 'breeder', caption: 'The breeder dashboard.' },
    ],
    planKey: 'breeder',
  },
  {
    slug: 'purchase-orders',
    name: 'Purchase orders',
    icon: ShoppingCart,
    tone: 'bg-indigo-100 text-indigo-700',
    summary: 'Raise orders for a farm, send them to suppliers, and match the bill in Xero.',
    headline: 'Every purchase raised for a farm, received, and matched to the bill',
    intro:
      'People you authorise raise purchase orders for a farm, email them to the supplier, and record what arrived. Accounts sees what was ordered, by whom and for which farm, and the order goes to your Xero so the supplier\'s bill is made from it.',
    forWhom: 'Anyone buying for the farm',
    highlights: [
      { title: 'Numbered by farm', body: 'Each order carries its farm\'s code in the number, so a bill can be traced back.' },
      { title: 'Receipts attached', body: 'Paid on a card? Photograph the receipt onto the order and accounts is told.' },
      { title: 'Stock received', body: 'Record what arrived against each line, in part or in full.' },
      { title: 'Xero purchase orders', body: 'Orders go to Xero approved, under your number, ready to copy into a bill.' },
    ],
    shots: [
      { shot: 'purchase-orders', caption: 'Purchase orders by farm, supplier and status.' },
      { shot: 'purchase-order', caption: 'An order with its lines, receipts and Xero status.' },
    ],
    freeNote: 'Included with every account. Your admin chooses who can raise orders.',
  },
  {
    slug: 'safety',
    name: 'Inductions, WHS & certifications',
    icon: HardHat,
    tone: 'bg-yellow-100 text-yellow-800',
    summary: 'Policies acknowledged, machines signed off, certificates tracked, hazards reported.',
    headline: 'Nobody on the farm is assumed to be trained in anything',
    intro:
      'Every new starter reads and acknowledges your induction pack on their phone. Supervisors sign people off on each machine, certificates and licences are tracked with expiry dates, and anyone can report a safety issue.',
    forWhom: 'Every employer',
    highlights: [
      { title: 'Induction pack', body: 'Policies acknowledged against a fixed version, with starter drafts for heat, chemicals, machinery, fatigue and more.' },
      { title: 'Equipment sign-offs', body: 'One induction per kind of machine, with hazards, controls and PPE, signed off by a supervisor and enforced at the machine.' },
      { title: 'Certifications', body: 'Chemical, first aid, forklift and other certificates with expiry reminders.' },
      { title: 'Report a safety issue', body: 'Hazards and incidents reported from the phone, with corrective actions followed through.' },
    ],
    shots: [
      { shot: 'my-inductions', caption: 'What each person has read and been signed off on.' },
      { shot: 'inductions', caption: 'The induction register for supervisors.' },
      { shot: 'certifications', caption: 'Certifications and when they expire.' },
    ],
    freeNote: 'Inductions and certifications are included with every account.',
  },
  {
    slug: 'compliance',
    name: 'Compliance & audit',
    icon: ClipboardCheck,
    tone: 'bg-fuchsia-100 text-fuchsia-700',
    summary: 'A season\'s records in one pack, and secure, time-limited access for your auditor.',
    headline: 'Audit day without the shoebox',
    intro:
      'Spray records, chemical stock, calibrations, operator credentials, training, policies and harvest come together in one season pack, frozen and fingerprinted when it is made. Give your auditor a secure, time-limited link instead of a pile of paper.',
    forWhom: 'Growers certified under schemes such as Freshcare or GLOBALG.A.P.',
    highlights: [
      { title: 'Season packs', body: 'A PDF and spray register CSV, frozen with a fingerprint, so a pack downloaded later is exactly what was handed over.' },
      { title: 'Auditor access', body: 'A link and an emailed code, for up to 90 days, revocable, with every view logged. No account needed.' },
      { title: 'Self-assessment', body: 'Work through the topics an audit covers, with the evidence FactreeTech already holds beside each one.' },
      { title: 'Only what is needed', body: 'People appear by name, role, credentials and training only: no contact details, pay or health information.' },
    ],
    shots: [
      { shot: 'compliance', caption: 'Compliance: season packs, audits and self-assessment.' },
    ],
    freeNote: 'Included for whoever manages spray, HR or harvest records.',
  },
];

/** Harvest bins are part of Farm Jobs; this is only for the home page's icon row. */
export const HARVEST_ICON = Apple;

export function featureBySlug(slug: string | undefined): Feature | undefined {
  return FEATURES.find((f) => f.slug === slug);
}

/** The feature page for a plan, for the pricing page's "What it does" links. */
export function featureForPlan(moduleKey: string): Feature | undefined {
  return FEATURES.find((f) => f.planKey === moduleKey);
}
